<script setup lang="ts">
import { computed, ref } from 'vue';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { useDentalAndVisionApplication } from '@/shared/infrastructure/composition/useDentalAndVisionApplication';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import QuestionSignature from '@/application/shared/infrastructure/components/questions/QuestionSignature.vue';

const props = defineProps<{
  applicationType: AppTypeDentalOrVision;
  prePopulate?: boolean;
}>();

const {
  dentalAndVisionApplicationStore,
  primaryApplicant,
  guardian,
  isChildOnlyApplication
} = useDentalAndVisionApplication(props.applicationType);

const signatureApplicant = computed(() => (isChildOnlyApplication.value && guardian.value ? guardian.value : primaryApplicant.value));
const isGuardian = computed(() => isChildOnlyApplication.value && !!guardian.value);

const signatureRef = ref<InstanceType<typeof QuestionSignature> | null>(null);

const updateSignature = (value: string): void => {
  dentalAndVisionApplicationStore.updateApplication({ signature: value }, props.applicationType);
};

interface EmitInterface {
  (event: 'submitForm'): void
}

const emits = defineEmits<EmitInterface>();

const sectionSubmit = async (): Promise<boolean | undefined> => {
  const localValid = await signatureRef.value?.handleSubmit();

  if (localValid) {
    emits('submitForm');
  }

  return undefined;
};

defineExpose({
  sectionSubmit
});
</script>
<template>
  <BaseSection title="Application Signature">
    <QuestionSignature v-if="signatureApplicant" :pre-populate="prePopulate" :custom-label="isGuardian ? 'Legal Guardian' : undefined" :first-name="signatureApplicant.first_name" :last-name="signatureApplicant.last_name" @signatureValid="updateSignature" ref="signatureRef"/>
  </BaseSection>
</template>
